import { useNestedQueryParamHelper, useQueryParamHelper } from '@shared/core/queryString';
import { useEffect, useMemo, useRef } from 'react';
import { processDestination, processLatLngValue, processDate, processRooms, processSearchRadius } from './paramValidation';

export type SearchParamsValue = {
  destination: ReturnType<typeof processDestination>;
  lat: ReturnType<typeof processLatLngValue>;
  lng: ReturnType<typeof processLatLngValue>;
  googlePlaceId: ReturnType<typeof processDestination>;
  startDate: ReturnType<typeof processDate>;
  endDate: ReturnType<typeof processDate>;
  rooms: ReturnType<typeof processRooms>;
};

export type Props = {
  onValueUpdate: (value: SearchParamsValue) => void;
};

type UseStaysSearchParamsArgs = {
  onValueUpdate?: (value: SearchParamsValue) => void;
};

const extractRelevantSearchParams = (helper: ReturnType<typeof useQueryParamHelper>) => {
  // latitude
  const lat = helper.getValueString('lat');
  // longitude
  const lng = helper.getValueString('lng');
  // display string of location
  const destination = helper.getValueString('destination');
  // dates - start
  const startDate = helper.getValueString('checkin') || helper.getValueString('startDate');
  // dates - end
  const endDate = helper.getValueString('checkout') || helper.getValueString('endDate');

  const searchRadius = helper.getValueInteger('radius');

  const googlePlaceId = helper.getValueString('googlePlaceId');

  /**
   * rooms are a special domain language rooms are comma separated occupants
   * e.g. a1:c6:c5,a2:c3
   * This means 2 rooms:
   * - 1st room has 1 adult and 2 children aged 6 and 5
   * - 2nd room has 2 adults and 1 child aged 3
   */
  const rooms = helper.getValueString('rooms');

  return {
    destination: processDestination(destination),
    lat: processLatLngValue(lat),
    lng: processLatLngValue(lng),
    searchRadius: processSearchRadius(searchRadius),
    googlePlaceId: processDestination(googlePlaceId),
    startDate: processDate(startDate),
    endDate: processDate(endDate),
    rooms: processRooms(rooms),
  };
};

/**
 * Shared logic for useStaysSearchParams and useNestedStaysSearchParams.
 * Stabilizes `rooms` to preserve referential equality when the underlying
 * query string hasn't changed, preventing downstream useMemo/useEffect churn.
 */
function useStaysSearchParamsFromHelper(helper: ReturnType<typeof useQueryParamHelper>, onValueUpdate?: (value: SearchParamsValue) => void) {
  const rawRoomsString = helper.getValueString('rooms') ?? undefined;
  const prevRawRef = useRef<string | undefined>(undefined);
  const prevRoomsRef = useRef<ReturnType<typeof processRooms> | null>(null);

  const baseValue = useMemo(() => extractRelevantSearchParams(helper), [helper]);

  // Preserve referential equality of rooms when the raw string hasn't changed
  if (rawRoomsString !== prevRawRef.current) {
    prevRawRef.current = rawRoomsString;
    prevRoomsRef.current = baseValue.rooms;
  }
  const stableRooms = prevRoomsRef.current ?? baseValue.rooms;

  const value = useMemo(() => ({ ...baseValue, rooms: stableRooms }), [baseValue, stableRooms]);

  useEffect(() => {
    onValueUpdate?.(value);
  }, [value, onValueUpdate]);

  return value;
}

const useStaysSearchParams = ({ onValueUpdate }: UseStaysSearchParamsArgs = {}) => {
  const helper = useQueryParamHelper();
  return useStaysSearchParamsFromHelper(helper, onValueUpdate);
};

const useNestedStaysSearchParams = ({ onValueUpdate, rootQueryParamKey }: UseStaysSearchParamsArgs & { rootQueryParamKey: string }) => {
  const helper = useNestedQueryParamHelper(rootQueryParamKey);
  return useStaysSearchParamsFromHelper(helper, onValueUpdate);
};

const SearchParamsObserver = ({ onValueUpdate }: Props) => {
  useStaysSearchParams({ onValueUpdate });
  return null;
};

export { useStaysSearchParams, SearchParamsObserver, useNestedStaysSearchParams };
